// ------------------------------
// components/SendUsdt.tsx
// Send USDT payment screen
// ------------------------------

import { observer } from "mobx-react";
import React, { useState, useEffect, useCallback } from "react";
import {
  Text,
  Center,
  Stack,
  Anchor,
  Title,
  Group,
  Button,
  TextInput,
  NumberInput,
  Alert,
  Box,
  Checkbox,
  Paper,
  ThemeIcon,
  Divider,
  Progress,
  Badge,
} from "@mantine/core";
import {
  IconSend2,
  IconReceipt,
  IconAlertTriangle,
  IconRefresh,
  IconCheck,
  IconExternalLink,
  IconCurrencyDollar,
  IconClock,
  IconBrandTether,
} from "@tabler/icons-react";

import { BobsWorkspace } from "../stores/BobsWorkspace";
import UsdtDisclaimer from "../helpers/UsdtDisclaimer";
import { DestinationAddress } from "./details/DestinationAddress";
import { formatFiat } from "../helpers/formatNumbers";

// USDT has 6 decimal places
const USDT_DECIMALS = 6;
const USDT_BASE_UNITS_PER_DOLLAR = 10 ** USDT_DECIMALS;

// Quote validity in seconds
const QUOTE_VALIDITY_SECONDS = 90;

// Convert USDT base units to display string
const baseUnitsToUsd = (baseUnits: number | null): string => {
  if (baseUnits === null || baseUnits === undefined) return "0.00";
  return (baseUnits / USDT_BASE_UNITS_PER_DOLLAR).toFixed(2);
};

// Convert USD to base units
const usdToBaseUnits = (usd: number): number => {
  return Math.round(usd * USDT_BASE_UNITS_PER_DOLLAR);
};

const SendUsdt = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const { selectedBob } = bobsWorkspace;
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [addressConfirmed, setAddressConfirmed] = useState(false);
    const [quoteCountdown, setQuoteCountdown] = useState<number>(
      QUOTE_VALIDITY_SECONDS,
    );

    // Reset function for starting over
    const resetProposal = useCallback(() => {
      selectedBob?.deleteAllSolanaUsdtProposals();
      selectedBob?.createAndSelectNewSolanaUsdtProposal();
      setAddressConfirmed(false);
      setQuoteCountdown(QUOTE_VALIDITY_SECONDS);
    }, [selectedBob]);

    // Initialize proposal on mount
    useEffect(() => {
      if (selectedBob) {
        selectedBob.createAndSelectNewSolanaUsdtProposal();
      }
      return () => {
        if (selectedBob) {
          selectedBob.deleteAllSolanaUsdtProposals();
        }
      };
    }, [selectedBob]);

    const proposal = selectedBob?.selectedSolanaUsdtProposal;

    // Countdown timer for quote expiration
    useEffect(() => {
      if (!proposal?.uuidFromServer) {
        setQuoteCountdown(QUOTE_VALIDITY_SECONDS);
        return;
      }

      const interval = setInterval(() => {
        const remaining = proposal.quoteTimeRemainingSeconds;
        setQuoteCountdown(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [proposal?.uuidFromServer, proposal?.quoteExpiresAt]);

    const handleAgree = async () => {
      await bobsWorkspace.agreeToStablecoinRisks();
    };

    // Show disclaimer if user hasn't agreed
    if (!bobsWorkspace.accountHasAgreedToStablecoinRisks) {
      return (
        <UsdtDisclaimer
          onAgree={handleAgree}
          buttonText="I Understand the USDT Risks"
        />
      );
    }

    // Show disclaimer on demand
    if (showDisclaimer) {
      return (
        <UsdtDisclaimer
          onAgree={() => setShowDisclaimer(false)}
          buttonText="Close"
        />
      );
    }

    const isQuoteExpired = proposal?.isQuoteExpired || quoteCountdown <= 0;

    return (
      <Stack>
        <Center>
          <Group gap="xs">
            <IconBrandTether size={28} />
            <Title order={3}>Send USDT</Title>
          </Group>
        </Center>
        {/* 
        {!proposal?.uuidFromServer && (
          <Center>
            <Text size="sm" c="dimmed">
              Send USDT on the Solana network.
            </Text>
          </Center>
        )} */}

        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
          title="USDT Risks"
        >
          <Text size="xs">
            This is an experimental feature. Only use it for small payment
            sizes.
          </Text>
          <Divider my="sm" />
          <Text size="xs">
            Please understand the risks of USDT. When sending to a new address,
            always start with a $1 test transaction.{" "}
            <Anchor
              style={{ cursor: "pointer" }}
              onClick={() => setShowDisclaimer(true)}
              size="xs"
              c="yellow"
              inherit
            >
              Learn more
            </Anchor>
          </Text>
        </Alert>

        {proposal && (
          <Stack gap="md" mt="md">
            {/* Address Entry - hide when quote received */}
            {!proposal.uuidFromServer && (
              <>
                {!proposal.validProposedAddress ? (
                  <Box>
                    <TextInput
                      label="Destination USDT Address (Solana Network)"
                      placeholder="Enter a USDT Solana address"
                      value={proposal.proposedAddress || ""}
                      onChange={(e) =>
                        proposal.setProposedAddress(e.currentTarget.value)
                      }
                      error={proposal.userFacingAddressErrorMessage}
                    />
                  </Box>
                ) : (
                  <DestinationAddress
                    address={proposal.validProposedAddress}
                    label="Destination Address (Solana)"
                    onClear={resetProposal}
                  />
                )}
              </>
            )}

            {/* Amount Entry - only show when address is valid and no quote yet */}
            {proposal.validProposedAddress && !proposal.uuidFromServer && (
              <Box>
                <NumberInput
                  label="Amount (USDT)"
                  placeholder="Enter amount in USDT"
                  value={
                    proposal.proposedAmountBaseUnits !== null
                      ? proposal.proposedAmountBaseUnits /
                        USDT_BASE_UNITS_PER_DOLLAR
                      : ""
                  }
                  onChange={(value) => {
                    if (typeof value === "number") {
                      proposal.setProposedAmountBaseUnits(
                        usdToBaseUnits(value),
                      );
                    } else {
                      proposal.setProposedAmountBaseUnits(null);
                    }
                  }}
                  min={1}
                  step={0.01}
                  decimalScale={2}
                  allowNegative={false}
                  prefix="$"
                  error={proposal.userFacingProposedAmountErrorMessage}
                />

                {proposal.proposedAmountBaseUnits !== null &&
                  proposal.proposedAmountBaseUnits > 0 &&
                  selectedBob && (
                    <Text size="sm" c="dimmed" mt="xs">
                      ≈{" "}
                      {Math.ceil(
                        (proposal.proposedAmountBaseUnits /
                          USDT_BASE_UNITS_PER_DOLLAR /
                          (bobsWorkspace.exchangeRates?.data?.usd || 100000)) *
                          100_000_000,
                      ).toLocaleString()}{" "}
                      sats
                    </Text>
                  )}
              </Box>
            )}

            {/* Get Quote Button */}
            {proposal.proposedAmountBaseUnitsIsValid &&
              !proposal.uuidFromServer && (
                <Button
                  loading={proposal.bob.areWeWaitingOnRails}
                  disabled={proposal.bob.areWeWaitingOnRails}
                  leftSection={<IconSend2 size={18} />}
                  size="md"
                  mt="md"
                  onClick={() => {
                    proposal.makeNewStablePaymentQuote();
                  }}
                >
                  Initiate USDT Payment
                </Button>
              )}

            {/* Quote Received - Show breakdown and confirmation */}
            {proposal.uuidFromServer &&
              !proposal.confirmedTransactionIdFromServer && (
                <Stack gap="md" mt="md">
                  {/* Quote Timer */}
                  <Paper p="sm" radius="md" withBorder>
                    <Group justify="space-between" align="center">
                      <Group gap="xs">
                        <IconClock size={16} />
                        <Text size="sm" fw={500}>
                          Quote expires in
                        </Text>
                      </Group>
                      <Badge
                        color={
                          quoteCountdown <= 10
                            ? "red"
                            : quoteCountdown <= 20
                              ? "yellow"
                              : "green"
                        }
                        size="lg"
                      >
                        {quoteCountdown} seconds
                      </Badge>
                    </Group>
                    <Progress
                      value={(quoteCountdown / QUOTE_VALIDITY_SECONDS) * 100}
                      color={
                        quoteCountdown <= 10
                          ? "red"
                          : quoteCountdown <= 20
                            ? "yellow"
                            : "green"
                      }
                      size="sm"
                      mt="xs"
                    />
                  </Paper>

                  {isQuoteExpired ? (
                    <Alert
                      icon={<IconAlertTriangle size={18} />}
                      color="red"
                      variant="light"
                      title="Quote Expired"
                    >
                      <Text size="sm" mb="md">
                        This quote has expired. Please request a new quote.
                      </Text>
                      <Button
                        variant="light"
                        color="red"
                        leftSection={<IconRefresh size={16} />}
                        onClick={resetProposal}
                      >
                        Get New Quote
                      </Button>
                    </Alert>
                  ) : (
                    <>
                      <Paper p="md" radius="md" withBorder>
                        <Stack gap="sm">
                          <Group gap="xs">
                            <ThemeIcon variant="light" color="blue" size="md">
                              <IconReceipt size={16} />
                            </ThemeIcon>
                            <Text fw={600}>Payment Summary</Text>
                          </Group>

                          <Divider />

                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              USDT to send
                            </Text>
                            <Text size="sm" fw={500}>
                              $
                              {baseUnitsToUsd(proposal.proposedAmountBaseUnits)}{" "}
                              USDT
                            </Text>
                          </Group>

                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              BTC equivalent
                            </Text>
                            <Stack gap={0} align="flex-end">
                              <Text size="sm" fw={500}>
                                {proposal.amountSatoshisFromServer?.toLocaleString()}{" "}
                                sats
                              </Text>
                              {selectedBob &&
                                proposal.amountSatoshisFromServer && (
                                  <Text size="xs" c="dimmed">
                                    {formatFiat({
                                      satoshis:
                                        proposal.amountSatoshisFromServer,
                                      currency: selectedBob.currencyCode,
                                      currencySnapshot:
                                        bobsWorkspace.exchangeRates?.data,
                                    })}
                                  </Text>
                                )}
                            </Stack>
                          </Group>

                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              Platform fee (
                              {(
                                bobsWorkspace.weKeepThisPlatformFeePercentageForStablePayments *
                                100
                              ).toFixed(0)}
                              % +{" "}
                              {
                                bobsWorkspace.bobStableSolanaFixedPlatformFeeSatoshis
                              }{" "}
                              sats)
                            </Text>
                            <Stack gap={0} align="flex-end">
                              <Text size="sm" fw={500}>
                                {proposal.platformFeeSatoshisFromServer?.toLocaleString()}{" "}
                                sats
                              </Text>
                              {selectedBob &&
                                proposal.platformFeeSatoshisFromServer && (
                                  <Text size="xs" c="dimmed">
                                    {formatFiat({
                                      satoshis:
                                        proposal.platformFeeSatoshisFromServer,
                                      currency: selectedBob.currencyCode,
                                      currencySnapshot:
                                        bobsWorkspace.exchangeRates?.data,
                                    })}
                                  </Text>
                                )}
                            </Stack>
                          </Group>

                          <Divider />

                          <Group justify="space-between">
                            <Text size="sm" fw={600}>
                              Total Debit
                            </Text>
                            <Stack gap={0} align="flex-end">
                              <Text size="sm" fw={600}>
                                {(
                                  (proposal.amountSatoshisFromServer || 0) +
                                  (proposal.platformFeeSatoshisFromServer || 0)
                                ).toLocaleString()}{" "}
                                sats
                              </Text>
                              {selectedBob && (
                                <Text size="xs" c="dimmed">
                                  {formatFiat({
                                    satoshis:
                                      (proposal.amountSatoshisFromServer || 0) +
                                      (proposal.platformFeeSatoshisFromServer ||
                                        0),
                                    currency: selectedBob.currencyCode,
                                    currencySnapshot:
                                      bobsWorkspace.exchangeRates?.data,
                                  })}
                                </Text>
                              )}
                            </Stack>
                          </Group>
                        </Stack>
                      </Paper>

                      <DestinationAddress
                        address={proposal.validProposedAddress!}
                        label="Destination Address (Solana)"
                        onClear={resetProposal}
                      />

                      <Alert
                        icon={<IconAlertTriangle size={18} />}
                        color="orange"
                        variant="light"
                      >
                        <Checkbox
                          checked={addressConfirmed}
                          onChange={(e) =>
                            setAddressConfirmed(e.currentTarget.checked)
                          }
                          label="I confirm the Solana address is correct and understand that USDT payments are irreversible."
                          styles={{ body: { cursor: "default" } }}
                        />
                      </Alert>

                      {/* 2FA Code Input */}
                      {bobsWorkspace.accountRequiresTwoFactorFormForActions && (
                        <TextInput
                          label="Two-Factor Authentication Code"
                          placeholder="Enter your 2FA code"
                          value={proposal.userProvided2FaCode || ""}
                          onChange={(e) =>
                            proposal.setUserProvided2FaCode(
                              e.currentTarget.value,
                            )
                          }
                          maxLength={6}
                        />
                      )}

                      <Button
                        leftSection={<IconSend2 size={18} />}
                        size="md"
                        onClick={() => {
                          proposal.sendSolanaUsdtPayment();
                        }}
                        color="green"
                        loading={proposal.bob.areWeWaitingOnRails}
                        disabled={
                          !addressConfirmed ||
                          proposal.bob.areWeWaitingOnRails ||
                          isQuoteExpired ||
                          (bobsWorkspace.accountRequiresTwoFactorFormForActions &&
                            (proposal.userProvided2FaCode?.length || 0) < 6)
                        }
                      >
                        Confirm & Send USDT
                      </Button>
                    </>
                  )}
                </Stack>
              )}

            {/* Transaction Confirmed */}
            {proposal.confirmedTransactionIdFromServer && (
              <Alert
                icon={<IconCheck size={18} />}
                color="green"
                variant="light"
                title="Transaction Sent"
              >
                <Stack gap="xs">
                  <Text size="sm">
                    Your USDT payment of $
                    {baseUnitsToUsd(proposal.proposedAmountBaseUnits)} has been
                    sent!
                  </Text>
                  <Anchor
                    href={`https://solscan.io/tx/${proposal.confirmedTransactionIdFromServer}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                  >
                    <Group gap={4}>
                      View on Solscan
                      <IconExternalLink size={14} />
                    </Group>
                  </Anchor>
                </Stack>
              </Alert>
            )}

            {/* Start Over Button */}
            {(proposal.userFacingAddressErrorMessage ||
              proposal.userFacingProposedAmountErrorMessage ||
              proposal.uuidFromServer) && (
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={resetProposal}
                >
                  Start Over
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);

export default SendUsdt;
